import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVerticalIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CommonList from "@/components/common/List";
import CommonTableList from "@/components/common/TableList";
import InventoryCreate from "./Create";
import InventoryEdit from "./Edit";
import InventoryDelete from "./Delete";
import { data, useNavigate } from "react-router-dom";
import { Inventory } from "@/interface/entityInterface";
import InventoryDetails from "./InventoryDetails";
import { ResourceContextProvider } from "react-admin";

const InvetoryList = () => {
  const navigate = useNavigate()
  const [detailsOpen, setDetailsOpen] = React.useState(false)
  const [inventory, setInventory] = React.useState<Inventory | null>(null)

  const openDetails = (e: any, row: Inventory) => {
    setDetailsOpen(true)
    setInventory(row)
  }
  
  const columns = React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => (
          <div className="capitalize">{row.getValue("id")}</div>
        ),
      },
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{row.getValue("name")}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "sku",
        header: "SKU",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{row.getValue("sku")}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{row.getValue("category")}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "price",
        header: "Price",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{row.getValue("price")}$</span>
            </div>
          );
        },
      },
      {
        accessorKey: "quantity",
        header: "Quantity",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{row.getValue("quantity")} {row.original?.unit}</span>
            </div>
          );
        },
      },
      {
        accessorFn: ({row}) => row.supplier.vendor_name || "",
        accessorKey: "supplier_id",
        header: "Supplier",
        cell: ({ row }) => {
          const supplier = row.original?.supplier
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{supplier?.vendor_name}</span>
            </div>
          );
        },
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                size="icon"
              >
                <MoreVerticalIcon />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              <InventoryEdit prop={row.original.id} />
              <DropdownMenuSeparator />
              <InventoryDelete prop={row.original.id} />
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [],
  );

  return (
    <ResourceContextProvider value="auto-service-inventory">
      <CommonList>
        <CommonTableList
          columns={columns}
          filterSearch="name"
          create={<InventoryCreate />}
          rowClick={openDetails}
          anotherButton={<Button onClick={() => navigate("/inventory/import")}>Import</Button>}
        />
      </CommonList>
      {detailsOpen && <InventoryDetails open={detailsOpen} setOpen={setDetailsOpen} product={inventory} />}
    </ResourceContextProvider>
  );
};

export default InvetoryList;
